import { AfterViewInit, ChangeDetectorRef, Component, DOCUMENT, DestroyRef, ElementRef, HostBinding, Input, ViewChild, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { EventModel, EventOption, EventOptionGroup } from '@frontend/sports/betting-offer/feature/model';
import { ImagesConfig, MarketGroupingConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { isDefined } from '@frontend/sports/common/core/utils/extended-types';
import { SourceOfPick } from '@frontend/sports/option/pick/feature/tracking';
import { LocalStoreService, WINDOW } from '@frontend/vanilla/core';
import { isEmpty, uniqBy } from 'lodash-es';
import { debounceTime, fromEvent } from 'rxjs';

import { DropdownOption } from '../../dropdown/models';
import {
    ANY_OTHER_SCORE_MARKET_TEXT,
    CLASS_LINE_SWITCHER_SWAP_DIALOG,
    CLASS_SCORECAST_OPTION_GROUP,
    DropdownImage,
    EventDetailsType,
    MultiColumnDropdownOption,
    SCORECAST_LOCAL_STORAGE_KEY,
    ScorecastStorageItem,
} from './scorecast-option-group.model';
import { ScorecastService } from './scorecast.service';

@Component({
    template: '',
})
export abstract class ScorecastWincastBaseComponent implements AfterViewInit {
    private changeDetectorRef = inject(ChangeDetectorRef);
    private elementRef = inject(ElementRef);
    private localStore = inject(LocalStoreService);
    private marketGroupingConfig = inject(MarketGroupingConfig);
    private sitecore = inject(Sitecore);
    protected scorecastService = inject(ScorecastService);
    protected imageConfig = inject(ImagesConfig);

    @Input() event: EventModel;
    @Input() optionGroup: EventOptionGroup;
    @Input() eventHelpText?: string;
    @Input() showMarketHelp = false;
    @Input() tracking: string;
    @Input() trackingModuleType = '';
    @Input() preselectedPick: { optionId: number } | null;
    @Input() sourceOfPick = SourceOfPick.Other;
    @Input() betDetails = '';

    @HostBinding('class') className = CLASS_SCORECAST_OPTION_GROUP;
    @ViewChild('dropdownWrapper') dropdownWrapper: ElementRef | undefined;

    readonly messages: {
        goalScorerTitle: string;
        scoreTitle: string;
        warningMessage: string;
        warningMessageKey: string;
        warningMessageForWinCastTwoway: string;
        warningMessageForWinCastTwowayKey: string;
        anyOtherScore: string;
    };

    players: MultiColumnDropdownOption[] = [];
    selectedPlayer: DropdownOption;
    activeEventOption?: EventOption;

    images: DropdownImage[] = [];
    cacheDuration: number;
    preSelected = false;
    playerMap: Record<number, string>;
    scrollParent: HTMLElement | Window;
    originalScrollPosition: { x: number; y: number } | undefined;
    dropdownMenuViewport = CLASS_SCORECAST_OPTION_GROUP;

    protected abstract get warningMessage(): string;

    private readonly destroyRef = inject(DestroyRef);
    private readonly _doc = inject(DOCUMENT);

    constructor() {
        this.cacheDuration = this.marketGroupingConfig.scorecastSelectionCacheDurationMs || 3600000;
        this.messages = {
            goalScorerTitle: this.sitecore.event.GoalScorerTitle || 'sitecore.event.GoalScorerTitle',
            scoreTitle: this.sitecore.event.ScoreTitle || 'sitecore.event.ScoreTitle',
            warningMessage: this.sitecore.event.WarnScorecastInvalidCombination,
            warningMessageKey: 'this.sitecore.event.WarnScorecastInvalidCombination',
            warningMessageForWinCastTwoway: this.sitecore.event.WarnWincastTwowayInvalidPlayer,
            warningMessageForWinCastTwowayKey: 'this.sitecore.event.WarnWincastTwowayInvalidPlayer',
            anyOtherScore: this.sitecore.event.AnyOtherScore || ANY_OTHER_SCORE_MARKET_TEXT,
        };
    }

    ngAfterViewInit(): void {
        this.screenWidthChanged(this.dropdownWrapper);
        fromEvent(this._window, 'resize')
            .pipe(debounceTime(100), takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.screenWidthChanged(this.dropdownWrapper));
    }

    onDropdownClick(expanded: boolean, eventDetailsType: EventDetailsType): void {
        if (expanded) {
            this.scorecastService.trackClick(eventDetailsType, this.optionGroup.periodName!);
            this.saveScrollPosition();
        }
    }

    protected onPlayerChange(market: DropdownOption): void {
        this.scorecastService.trackSelect(market.name!, EventDetailsType.Goalscorer, this.optionGroup.periodName!);

        this.setActiveOption();

        this.saveItemsToStorage(this.mapStorageItems());
        this.restoreScrollPosition();
    }

    protected setActiveOption(): void {
        this.activeEventOption = this.optionGroup?.options.find(
            (option) => option.optionGroupId.toString() === this.playerMap[this.selectedPlayer.id as number],
        );
    }

    protected initSelectedOptionsFromPick(): void {
        this.dropdownMenuViewport = CLASS_LINE_SWITCHER_SWAP_DIALOG;

        const option = this.getEventOptionFromPick();
        if (!option) {
            return;
        }

        const fixtureParticipantId = this.getFixtureParticipantById(option.optionGroupId);
        if (!fixtureParticipantId) {
            return;
        }

        this.selectedPlayer = this.getPlayerById(fixtureParticipantId)!;
        this.preSelected = !!this.selectedPlayer;
    }

    protected initSelecedOptionsFromStorage(): void {
        const persistedSelection = this.getPersistedSelectionFromStorage();

        if (persistedSelection) {
            this.selectedPlayer = this.getPlayerById(persistedSelection.player)!;
            this.preSelected = !!this.selectedPlayer;
        }
    }

    protected mapStorageItems(): ScorecastStorageItem {
        return {
            id: this.optionGroup.id,
            player: this.selectedPlayer.id.toString(),
            expiryDate: Date.now() + this.cacheDuration,
        };
    }

    mapParticipantImages(): void {
        this.images = this.event.participants.map((participant) => ({
            id: participant.id!.toString(),
            sportId: this.event.sport.id,
            ...participant.image,
        }));
    }

    getPlayersMap(): Record<number, string> {
        const result: Record<number, string> = {};

        if (!this.optionGroup) {
            return result;
        }

        uniqBy(this.optionGroup.options, 'optionGroupId').forEach((option) => {
            const originalGroup = this.event.optionGroups.find((group) => group.id === option.optionGroupId.toString());

            if (originalGroup?.fixtureParticipantId && originalGroup?.visible) {
                result[originalGroup.fixtureParticipantId] = originalGroup.id;
            }
        });

        return result;
    }

    mapPlayerGroups(): MultiColumnDropdownOption[] {
        return this.event.participants.map((team) => ({
            id: team.id!,
            name: team.name,
            payload: this.event.players
                .filter((player) => player.teamId === team.id)
                .map((player) => ({
                    id: player.fixtureParticipantId!.toString(),
                    name: player.name,
                    teamId: player.teamId,
                    disabled: !this.playerMap[player.fixtureParticipantId!],
                    order:
                        this.optionGroup.options
                            .filter((o) => o.visible)
                            .find((o) => o.optionGroupId.toString() === this.playerMap[player.fixtureParticipantId!])
                            ?.nativePrice.odds.toNumber() || 10000000,
                }))
                .sort((a, b) => a.order - b.order),
        }));
    }

    preSelectOptions(): void {
        if (this.preselectedPick) {
            this.initSelectedOptionsFromPick();
        } else {
            this.initSelecedOptionsFromStorage();
        }
    }

    getActiveWithFallback(options: MultiColumnDropdownOption[], activePredicate: (dropdownOption: DropdownOption) => boolean): DropdownOption {
        return (
            options
                .map((x) => x.payload.find((y) => activePredicate(y)))
                .filter(isDefined)
                .shift() ?? options[0].payload[0]
        );
    }

    getPersistedSelectionFromStorage(): ScorecastStorageItem | undefined {
        const persistedSelections = this.localStore.get<ScorecastStorageItem[]>(SCORECAST_LOCAL_STORAGE_KEY)!;
        if (isEmpty(persistedSelections)) {
            return;
        }

        const validSelections = persistedSelections.filter((item) => item.expiryDate > Date.now());
        this.localStore.set(SCORECAST_LOCAL_STORAGE_KEY, validSelections);

        return validSelections.find((sel) => sel.id === this.optionGroup.id);
    }

    saveItemsToStorage(selection: ScorecastStorageItem): void {
        const items = this.localStore.get<ScorecastStorageItem[]>(SCORECAST_LOCAL_STORAGE_KEY)!;

        if (isEmpty(items)) {
            this.localStore.set(SCORECAST_LOCAL_STORAGE_KEY, [selection]);

            return;
        }

        const index = items.findIndex((item) => item.id === selection.id);
        if (index !== -1) {
            items[index] = selection;
        } else {
            items.push(selection);
        }

        this.localStore.set(SCORECAST_LOCAL_STORAGE_KEY, items);
    }

    getLowestPriceOption(): EventOption {
        return (
            this.optionGroup.options
                .filter((o) => o.visible)
                .sort(this.sortByNativePrice)
                .shift() || this.optionGroup.options[0]
        );
    }

    getFixtureParticipantById(optionGroupId: number): string | undefined {
        return Object.entries(this.playerMap).find((entry) => entry[1] === optionGroupId.toString())?.[0];
    }

    getPlayerById(playerId: string): DropdownOption | undefined {
        return this.players.flatMap((p) => p.payload).find((p) => p.id === playerId);
    }

    getEventOptionFromPick(): EventOption | undefined {
        return this.optionGroup.options.find((eventOption) => eventOption.id === this.preselectedPick?.optionId);
    }

    sortByNativePrice(a: EventOption, b: EventOption): number {
        return a.nativePrice.equal(b.nativePrice) ? 0 : a.nativePrice.greaterThan(b.nativePrice) ? 1 : -1;
    }

    replaceDelimterValues(name: string): string {
        return name.replace(new RegExp(this.marketGroupingConfig.scorecastRegexValues.nameDelimterReplace), '-');
    }

    matchByName(option: EventOption, dropdownOption: DropdownOption): boolean {
        return (
            this.replaceDelimterValues(option.name).match(new RegExp(this.marketGroupingConfig.scorecastRegexValues.matchByName))?.toString() ===
            this.replaceDelimterValues(dropdownOption.name!)
        );
    }

    hasScore(name: string): boolean {
        return new RegExp(this.marketGroupingConfig.scorecastRegexValues.hasScore).test(name);
    }

    restoreScrollPosition(): void {
        if (this.originalScrollPosition !== undefined) {
            this.scrollParent.scrollTo(this.originalScrollPosition.x, this.originalScrollPosition.y);
            this.originalScrollPosition = undefined;
        }
    }

    saveScrollPosition(): void {
        this.scrollParent = this.getScrollParent(this.elementRef.nativeElement);

        this.originalScrollPosition = {
            x: this.scrollParent instanceof Window ? this.scrollParent.scrollX : this.scrollParent.scrollLeft,
            y: this.scrollParent instanceof Window ? this.scrollParent.scrollY : this.scrollParent.scrollTop,
        };
    }

    private screenWidthChanged(dropdownWrapper: ElementRef | undefined): void {
        if (this._doc.body.clientWidth > 767) {
            dropdownWrapper?.nativeElement.classList.remove('single-column-layout');
        } else {
            dropdownWrapper?.nativeElement.classList.add('single-column-layout');
        }

        this.changeDetectorRef.markForCheck();
    }

    private getScrollParent(node: HTMLElement | null): HTMLElement | Window {
        if (node == null || !(node instanceof HTMLElement)) {
            return this._window;
        }

        const style = getComputedStyle(node);
        const overflow: string = style && style['overflowY'];

        if (node.scrollHeight > node.clientHeight && (overflow === 'scroll' || overflow === 'auto')) {
            return node;
        } else {
            return this.getScrollParent(node.parentNode as HTMLElement);
        }
    }

    private readonly _window = inject(WINDOW);
}
